import { z } from 'zod';
import { getTariffVoltageType } from '@utils/tariff-helpers';
import { addIssue } from './add-issue';
import { validateRequiredField } from './required-field';
import { validateOperationHours } from './operation-hours';
import { validateACRPreset, validateACLClarkePreset, validateACLCompetitorPreset } from './preset-validators';
import { validateEnergyPrices } from './validateEnergyPrices';
import { getRequiredYears } from './getRequiredYears';
import { hasEmptyPrice } from './common';
import { getContextualErrorMessage } from './contextual-error-message';

export interface ValidateUnitPresetProps {
  unitPreset: any;
  basePath: Array<string | number>;
  ctx: {
    addIssue: (issue: z.IssueData) => void;
  };
  useDifferentPeriodsByUnit: boolean;
  globalSupplyStartDate?: string | null | Date;
  globalSupplyFinalDate?: string | null | Date;
  energyPricesProjection?: Record<string, number> | null;
  marginFeeProjection?: Record<string, number> | null;
}

export const validateUnitPreset = (props: ValidateUnitPresetProps): boolean => {
  const {
    unitPreset,
    basePath,
    ctx,
    useDifferentPeriodsByUnit,
    globalSupplyStartDate,
    globalSupplyFinalDate,
    energyPricesProjection,
  } = props;
  let hasErrors = false;
  const presetType = unitPreset?.presetType;
  const presetPayload = unitPreset?.presetPayload;
  const unitPayload = unitPreset?.unitPayload;
  const aux__useOpenInWeekends = presetPayload?.aux__useOpenInWeekends ?? false;

  if (!presetType) {
    addIssue(ctx, [...basePath, 'presetType'], getContextualErrorMessage(basePath, 'Tipo de cenário é obrigatório'));
    return false;
  }

  validateRequiredField(
    unitPayload?.nickname,
    [...basePath, 'unitPayload', 'nickname'],
    'Nome da unidade é obrigatório',
    ctx,
  );

  if (useDifferentPeriodsByUnit) {
    validateRequiredField(
      unitPayload?.supplyStartDate,
      [...basePath, 'unitPayload', 'supplyStartDate'],
      'Data de início é obrigatória',
      ctx,
    );
    validateRequiredField(
      unitPayload?.supplyFinalDate,
      [...basePath, 'unitPayload', 'supplyFinalDate'],
      'Data de término é obrigatória',
      ctx,
    );
  }

  const voltageTypeForHours = getTariffVoltageType(String(presetPayload?.tariffGroup));
  if (voltageTypeForHours === 'LOW_VOLTAGE') {
    const operationHoursRange = (presetPayload?.consumptionInfo as Record<string, any>)?.operationHoursRange;

    if (
      !validateOperationHours(operationHoursRange, 'business', [...basePath, 'presetPayload', 'consumptionInfo'], ctx)
    ) {
      hasErrors = true;
    }
    if (aux__useOpenInWeekends) {
      if (
        !validateOperationHours(operationHoursRange, 'saturday', [...basePath, 'presetPayload', 'consumptionInfo'], ctx)
      ) {
        hasErrors = true;
      }
      if (
        !validateOperationHours(operationHoursRange, 'sunday', [...basePath, 'presetPayload', 'consumptionInfo'], ctx)
      ) {
        hasErrors = true;
      }
    }
  }

  const presetBasePath = [...basePath, 'presetPayload'];
  switch (presetType) {
    case 'ACR':
      if (!validateACRPreset(presetPayload, presetBasePath, ctx)) {
        hasErrors = true;
      }
      break;
    case 'CLARKE_ACL':
      const hasRawPriceList =
        presetPayload && presetPayload.rawEnergyPrice && presetPayload.rawEnergyPrice.rawPriceList;
      if (hasRawPriceList) {
        const supplyStartDate = useDifferentPeriodsByUnit ? unitPayload?.supplyStartDate : globalSupplyStartDate;
        const supplyFinalDate = useDifferentPeriodsByUnit ? unitPayload?.supplyFinalDate : globalSupplyFinalDate;
        if (
          !validateEnergyPrices({
            rawPriceList: presetPayload.rawEnergyPrice.rawPriceList,
            basePath: [...presetBasePath, 'rawEnergyPrice', 'rawPriceList'],
            ctx,
            energyPricesProjection,
            supplyStartDate,
            supplyFinalDate,
            globalSupplyStartDate,
            globalSupplyFinalDate,
            aux__useDifferentPeriodsByUnit: useDifferentPeriodsByUnit,
          })
        ) {
          hasErrors = true;
        }
      } else {
        addIssue(
          ctx,
          [...presetBasePath, 'rawEnergyPrice', 'rawPriceList'],
          getContextualErrorMessage(basePath, 'Preços de energia são obrigatórios'),
        );
        hasErrors = true;
      }
      const marginValues = presetPayload?.marginFee?.values as Record<string, any> | undefined;
      const requiredYears = getRequiredYears(
        useDifferentPeriodsByUnit ? unitPayload?.supplyStartDate : undefined,
        useDifferentPeriodsByUnit ? unitPayload?.supplyFinalDate : undefined,
        globalSupplyStartDate,
        globalSupplyFinalDate,
        useDifferentPeriodsByUnit,
      );
      const isTargetScenario = basePath.some((p) => typeof p === 'string' && p.includes('targetScenarios'));
      const shouldValidateMarginFee =
        presetPayload?.marginFee && (!isTargetScenario || presetPayload?.aux__useCustomMargin === true);
      if (shouldValidateMarginFee) {
        if (!marginValues) {
          addIssue(
            ctx,
            [...presetBasePath, 'marginFee', 'values'],
            getContextualErrorMessage(basePath, 'Valores de margem são obrigatórios'),
          );
          hasErrors = true;
        } else {
          requiredYears.forEach((yearKey) => {
            const marginValue = marginValues[yearKey];
            if (hasEmptyPrice(marginValue)) {
              addIssue(
                ctx,
                [...presetBasePath, 'marginFee', 'values', yearKey],
                `Margem para ${yearKey.replace('year', '')} é obrigatória`,
              );
              hasErrors = true;
            } else if (typeof marginValue !== 'number' || isNaN(marginValue)) {
              addIssue(
                ctx,
                [...presetBasePath, 'marginFee', 'values', yearKey],
                `Margem para ${yearKey.replace('year', '')} deve ser um número válido`,
              );
              hasErrors = true;
            }
          });
        }
      }
      if (!validateACLClarkePreset(presetPayload, presetBasePath, ctx)) {
        hasErrors = true;
      }
      break;
    case 'COMPETITOR_ACL':
      if (!validateACLCompetitorPreset(presetPayload, presetBasePath, ctx)) {
        hasErrors = true;
      }
      break;
    default:
      addIssue(ctx, [...basePath, 'presetType'], getContextualErrorMessage(basePath, 'Tipo de cenário inválido'));
      hasErrors = true;
  }

  return !hasErrors;
};
